// Opportunities (issues) view — vote-ordered table per tab. Convert
// flows live on the row's action menu (kept minimal for the BSP port).
import { useState } from "react";
import { ChevronUp, Plus } from "lucide-react";
import { tpApi } from "../api";
import { useResource } from "../hooks";
import { formatDateShort, priorityChipClass, statusChipClass } from "../utils";
import { Avatar } from "../components/Avatar";
import { IssueSheet } from "../sheets/IssueSheet";
import { usePlannerCreate, usePlannerRefreshKey } from "../sheets/PlannerCreateContext";
import type { Issue } from "../../../../../../shared/team-planner/schemas";

const TABS = [
  { id: "open", label: "Open" },
  { id: "actioned", label: "Actioned" },
  { id: "converted", label: "Converted" },
  { id: "archived", label: "Archived" },
] as const;
type Tab = (typeof TABS)[number]["id"];

export function OpportunitiesView() {
  const [tab, setTab] = useState<Tab>("open");
  const [openId, setOpenId] = useState<string | null>(null);
  const openCreate = usePlannerCreate();
  const refreshKey = usePlannerRefreshKey();
  const issues = useResource(() => tpApi.listIssues(tab), [tab, refreshKey]);

  async function vote(issue: Issue) {
    await tpApi.voteIssue(issue.id, issue.viewerHasVoted ? "unvote" : "vote");
    issues.refresh();
  }

  return (
    <>
      <div className="tp-toolbar">
        <div className="tp-toolbar-left">
          <div className="tp-scope">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                aria-checked={tab === t.id}
                role="radio"
                onClick={() => setTab(t.id)}
                className="tp-scope-btn"
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="tp-toolbar-right">
          <button
            type="button"
            className="bsp-btn bsp-btn-primary"
            onClick={() => openCreate("opportunity")}
          >
            <Plus size={14} /> Raise opportunity
          </button>
        </div>
      </div>

      {issues.error ? (
        <p style={{ color: "var(--bsp-red-fg)" }}>Failed to load: {issues.error}</p>
      ) : null}

      <div className="bsp-card" style={{ padding: 0 }}>
        <table className="bsp-table">
          <thead>
            <tr>
              <th style={{ width: 80 }}>Votes</th>
              <th style={{ width: 80 }}>Seq</th>
              <th>Title</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Raised by</th>
              <th>Raised</th>
            </tr>
          </thead>
          <tbody>
            {(issues.data ?? []).map((i) => (
              <tr key={i.id} style={{ cursor: "pointer" }} onClick={() => setOpenId(i.id)}>
                <td onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className={`bsp-btn bsp-btn-sm ${i.viewerHasVoted ? "bsp-btn-primary" : "bsp-btn-secondary"}`}
                    onClick={() => vote(i)}
                    title={i.viewerHasVoted ? "Unvote" : "Vote"}
                  >
                    <ChevronUp size={12} /> {i.voteCount}
                  </button>
                </td>
                <td className="bsp-mono">O{i.seq}</td>
                <td>{i.title}</td>
                <td>
                  <span className={statusChipClass(i.status)}>{i.status}</span>
                </td>
                <td>
                  <span className={priorityChipClass(i.priority)}>{i.priority}</span>
                </td>
                <td>
                  {i.isAnonymous ? (
                    <span style={{ color: "var(--bsp-fg-muted)" }}>Anonymous</span>
                  ) : i.raisedBy ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Avatar owner={i.raisedBy} />
                      {i.raisedBy.displayName}
                    </span>
                  ) : (
                    <span style={{ color: "var(--bsp-fg-muted)" }}>Unknown</span>
                  )}
                </td>
                <td>{formatDateShort(i.raisedAt)}</td>
              </tr>
            ))}
            {(issues.data ?? []).length === 0 && !issues.loading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", color: "var(--bsp-fg-muted)" }}>
                  No opportunities in this tab.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {openId ? (
        <IssueSheet
          id={openId}
          tab={tab}
          onClose={() => setOpenId(null)}
          onChanged={() => issues.refresh()}
        />
      ) : null}
    </>
  );
}
